import { motion, useTransform, MotionValue } from "motion/react";
import { ResultOf } from "gql.tada";
import {
  documentToReactComponents,
  Options,
} from "@contentful/rich-text-react-renderer";
import {
  BLOCKS,
  MARKS,
  Document as RichTextDocument,
} from "@contentful/rich-text-types";
import { RichText } from "../../shared/core/RichText";
import { isValidPalette } from "../../../types/ThemeModel";
import { AnimatedSplitLines } from "../../shared/AnimatedSplitLines";
import { ThemeContextProvider } from "../../providers/ThemeContextProvider";
import { IntroBenefitsSectionById } from "../../../queries/entries/IntroBenefitsSectionById";
import { useIsDesktop } from "../../../hooks/useIsDesktop";

const ALLOWABLE_OFFSET_MOBILE = 150;
const ALLOWABLE_OFFSET_DESKTOP = 150;

export const AnimatedResponsiveDetails = ({
  benefitItem,
  scrollYProgress,
  index,
  total,
}: {
  scrollYProgress: MotionValue<number>;
  index: number;
  total: number;
  benefitItem: NonNullable<
    NonNullable<
      ResultOf<typeof IntroBenefitsSectionById>["introBenefitsSection"]
    >["benefitItemsCollection"]
  >["items"][number];
}) => {
  const palette = benefitItem?.themeColor?.palette;
  const mappedProgress = useTransform(
    () => scrollYProgress.get() * (total + 1)
  );

  const isDesktop = useIsDesktop();
  const ALLOWABLE_OFFSET = isDesktop
    ? ALLOWABLE_OFFSET_DESKTOP
    : ALLOWABLE_OFFSET_MOBILE;

  const splitLinesValue = useTransform(
    mappedProgress,
    [0, index, index + 0.5, index + 1, index + 2],
    [-1, -1, 0, 0, 1]
  );

  const factSourceOpacity = useTransform(
    mappedProgress,
    [index, index + 0.5, index + 1, index + 1.5, index + 2],
    [0, 0, 0.5, 0, 0]
  );

  if (!isValidPalette(palette)) return null;

  return (
    <div
      className="absolute left-0 top-1/2 max-w-full grid-container w-full"
      style={{
        transform: `translateY(calc(-50% + ${
          ALLOWABLE_OFFSET * (index / (total - 1) - 0.25)
        }px))`,
      }}
    >
      {!!benefitItem?.factSource && (
        <motion.div
          className="absolute left-0 bottom-[-32px] dsk:col-span-17 col-span-10 dsk:col-start-2 col-start-1"
          style={{
            opacity: factSourceOpacity,
          }}
        >
          <RichText content={benefitItem.factSource} />
        </motion.div>
      )}
      <ThemeContextProvider
        className="dsk:col-start-2 dsk:col-span-17 col-span-10"
        theme={{
          palette: palette,
          background: "neutral-light",
          foreground: "highlight",
        }}
      >
        <p className="text-left dsk:text-heading-2 text-heading-3">
          <AnimatedSplitLines value={splitLinesValue}>
            {documentToReactComponents(
              (benefitItem?.info?.json ?? {}) as RichTextDocument,
              highlightedTextRenderOptions
            )}
          </AnimatedSplitLines>
        </p>
      </ThemeContextProvider>
    </div>
  );
};

const highlightedTextRenderOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <span className="text-brand-cream">{children}</span>
    ),
  },
  renderMark: {
    [MARKS.ITALIC]: (text) => (
      <span className="text-palette-current-highlight">{text}</span>
    ),
  },
};
